/**
 * URL validation shared by the Sites management forms (footer links, pages,
 * featured custom link, logo / favicon, content external URL). Mirrors what
 * the runtime accepts: absolute http(s) URLs, plus `/`-relative paths where
 * the contract allows them (footer links).
 */

const HTTP_SCHEME = /^https?:\/\//i

/** True for an absolute `http(s)://` URL the URL parser accepts. */
export function isHttpUrl(value) {
	const s = String(value ?? '').trim()
	if (!HTTP_SCHEME.test(s) || /\s/.test(s)) return false
	try {
		const { hostname } = new URL(s)
		return hostname === 'localhost' || (Boolean(hostname) && hostname.includes('.'))
	} catch {
		return false
	}
}

/** True for a `/path` (single leading slash, no whitespace, not `//host`). */
export function isRelativePath(value) {
	const s = String(value ?? '').trim()
	return /^\/(?!\/)\S*$/.test(s)
}

/**
 * Inline error for a URL field, or `null` when valid / empty.
 * @param {string} value
 * @param {{ allowRelative?: boolean, required?: boolean }} [opts]
 */
export function urlError(value, { allowRelative = false, required = false } = {}) {
	const s = String(value ?? '').trim()
	if (!s) return required ? 'Enter a URL' : null
	if (isHttpUrl(s)) return null
	if (allowRelative && isRelativePath(s)) return null
	if (allowRelative) return 'Use a full URL (https://…) or a /path on this site'
	if (/^[a-z][a-z0-9+.-]*:/i.test(s) && !HTTP_SCHEME.test(s)) return 'Only http:// and https:// links are allowed'
	if (!HTTP_SCHEME.test(s)) return 'Start with https:// (or http://)'
	return 'This does not look like a valid URL'
}

/** True when the field is empty or passes `urlError`. */
export function isValidUrl(value, opts) {
	return urlError(value, opts) === null
}

/**
 * The address to show viewers for a site, without a trailing slash: the
 * promoted custom domain once there is one (castr `canonicalUrl` /
 * `canonicalHost`), else the Castr address (`url`), which then redirects.
 */
export function siteAddress(site) {
	if (!site) return ''
	const url = site.canonicalUrl || (site.canonicalHost ? `https://${site.canonicalHost}` : site.url)
	return url ? String(url).replace(/\/+$/, '') : ''
}
